// RITA-1549 — Eine gedruckte PDF aus der geschützten Ablage holen (GET /files?file=<uuid>.pdf, Berater-Dokumente).
//
// Der Abruf läuft außerhalb von GraphQL mit derselben Anmeldung wie die übrigen geschützten Dateien
// (authorizationHeader, RITA-1538). Die Adressform mit genau einem Parameter `file` ist Vertrag mit Dev-Proxy und
// nginx, die ihn in den Pfad umschreiben. Fehlerantworten werden in feste Nutzertexte übersetzt (Muster
// features/templates/api/vertragsvorlagePdf.ts); technische Einzelheiten stehen nie im Text.
import { ANMELDUNG_ABGELAUFEN_HINWEIS, istAnmeldungAbgelaufen } from '../../../shared/services/anmeldungAbgelaufen';
import authorizationHeader from '../../../shared/services/authorizationHeader';
import { DATEI_GELOESCHT_FEHLER } from '../services/vertragsdruckTexte';

export const VERTRAGSDATEI_ENDPUNKT = '/files';

export type VertragsdateiFehlercode = 'anmeldung' | 'rechte' | 'geloescht' | 'allgemein';

export const VERTRAGSDATEI_FEHLER: Record<VertragsdateiFehlercode, string> = {
  /** Wie bei der geschützten Ablage (RITA-1538, Variante B): Hinweis statt Abmeldung. */
  anmeldung: ANMELDUNG_ABGELAUFEN_HINWEIS,
  rechte: 'Keine Berechtigung, diese Datei zu öffnen.',
  /** 404: Die Datei wurde unter „Dokumente“ gelöscht; das Aktionsprotokoll zeigt es nach dem Neuladen. */
  geloescht: DATEI_GELOESCHT_FEHLER,
  allgemein: 'Die Datei konnte nicht geöffnet werden. Bitte erneut versuchen.',
};

export class VertragsdateiFehler extends Error {
  readonly code: VertragsdateiFehlercode;

  readonly status: number | null;

  constructor(code: VertragsdateiFehlercode, status: number | null) {
    super(VERTRAGSDATEI_FEHLER[code]);
    this.name = 'VertragsdateiFehler';
    this.code = code;
    this.status = status;
    // Zielumgebung ES5: die Prototypkette von Error-Unterklassen von Hand setzen (sonst versagt instanceof).
    Object.setPrototypeOf(this, VertragsdateiFehler.prototype);
  }
}

const codeFuerStatus = (status: number): VertragsdateiFehlercode => {
  if (istAnmeldungAbgelaufen(status)) return 'anmeldung';
  if (status === 403) return 'rechte';
  if (status === 404) return 'geloescht';
  return 'allgemein';
};

/** Adresse der Datei in der geschützten Ablage, wie sie auch die Liste „Dokumente“ bildet. */
export const vertragsdateiAdresse = (filename: string) =>
  `${VERTRAGSDATEI_ENDPUNKT}?file=${encodeURIComponent(filename)}`;

/** Lädt die PDF und liefert sie als Blob (Typ application/pdf); bei Fehlern ein VertragsdateiFehler mit Nutzertext. */
export async function vertragsdateiAbrufen(
  filename: string,
  fetchImpl: typeof fetch = (eingabe, init) => fetch(eingabe, init),
): Promise<Blob> {
  let antwort: Response;
  try {
    antwort = await fetchImpl(vertragsdateiAdresse(filename), { headers: authorizationHeader() });
  } catch (error) {
    throw new VertragsdateiFehler('allgemein', null);
  }
  if (!antwort.ok) throw new VertragsdateiFehler(codeFuerStatus(antwort.status), antwort.status);
  const blob = await antwort.blob();
  // Der Blob-Typ bestimmt, wie der neue Tab die Datei anzeigt; die Ablage liefert application/pdf.
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}

export default vertragsdateiAbrufen;
